import { v4 as uuidV4 } from 'uuid';
import { useCart } from '~/composables/custom/useCart/useCart';
import useUiNotification from '~/composables/useUiNotification';
import { useAppLocale } from '~/composables/custom/useAppLocale';
import { useTracking } from '~/composables/custom/useTracking';
import { useBFF } from '~/composables/useBFF/useBFF';
import { useUser } from '~/composables/custom/useUser/useUser';
import addressGetters from '~/composables/getters/addressGetters';
import type { Address } from '~/types/GraphQL';

export const useCheckout = () => {
  const errors = useState<string>('errors-in-checkout');
  const updating = useState('updating-user-data-in-checkout');

  const { user, isAuthenticated, setUser } = useUser();
  const { cart, setCart, refresh } = useCart();
  const { $checkout } = useBFF();
  const { send } = useUiNotification();
  const { country } = useAppLocale();
  const { trackError } = useTracking();
  const { $config } = useNuxtApp();

  async function setData(data: any) {
    if (!data.cart) {
      await refresh();
    }

    if (data?.cart) {
      await setCart({
        ...cart.value,
        ...data?.cart,
      });
    }

    if (data?.customer) {
      setUser({
        ...user.value,
        ...data.customer,
      });
    }
  }

  const getAddressWithDefault = (address: Address) => {
    const toAddress = addressGetters.transformToFormAddress(address, $config.public.i18n.countries);

    return {
      ...toAddress,
      key: toAddress.key || uuidV4(),
      country: toAddress.country || country,
      email: toAddress.email || user.value?.email,
      streetName: toAddress.streetName,
      streetNumber: toAddress.streetNumber,
      additionalStreetInfo: toAddress.additionalStreetInfo || '',
      // Packstation custom fields
      packstationNumber: toAddress.packstationNumber,
      packstationPostNumber: toAddress.packstationPostNumber,
      pickupLocationInformation: toAddress.pickupLocationInformation || '',
    };
  };

  const saveBillingStep = async ({ address, isShippingSame, isNewAddress }: { address: any; isShippingSame: boolean; isNewAddress: boolean }) => {
    updating.value = true;

    try {
      if (!address) {
        errors.value = 'cant continue. no billing address was defined.';
        trackError('saving billing info', errors.value);
        return;
      }

      errors.value = '';

      const { data } = await $checkout.saveBillingInfo({
        address: getAddressWithDefault(address),
        userVersion: user.value?.version,
        cartId: cart.value?.id,
        cartVersion: cart.value?.version,
        shipToBilling: isShippingSame,
        isNewAddress: isNewAddress && isAuthenticated.value,
      });

      await setData(data);
    }
    catch {
      errors.value = 'There was some error while trying to select this shipping method. We are sorry, please try with different shipping method or later.';
      trackError('saving billing info', errors.value);
      send({
        message: 'An error occured',
        type: 'danger',
      });
    }
    finally {
      updating.value = false;
    }
  };

  const saveShippingStep = async ({ address, isNewAddress }: { address: any; isNewAddress: boolean }) => {
    updating.value = true;
    try {
      if (!address) {
        errors.value = 'cant continue. no shipping address was defined.';
        trackError('saving shipping info', errors.value);
        return;
      }

      errors.value = '';

      const { data } = await $checkout.saveShippingInfo({
        address: getAddressWithDefault(address),
        userVersion: user.value?.version,
        cartId: cart.value?.id,
        cartVersion: cart.value?.version,
        isNewAddress: isNewAddress && isAuthenticated.value,
      });

      await setData(data);
    }
    catch {
      errors.value = 'There was some error while saving your shipping address. We are sorry, please try again later';
      trackError('saving shipping info', errors.value);
      send({
        message: 'There was some error while trying to select this shipping method. We are sorry, please try with different shipping method or later',
        type: 'danger',
      });
    }
    finally {
      updating.value = false;
    }
  };

  return {
    errors: computed(() => errors.value),
    updating: computed(() => updating.value),
    resetErrors: () => { errors.value = ''; },
    saveBillingStep,
    saveShippingStep,
  };
};
